import { Box, Grid } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { ExpandMinimizeButton } from '@tassat/ag-grid';
import { ToolbarButton } from '@tassat/button';
import { ActionTypes } from '@tassat/common-definitions';
import { AddCounterpartyForm } from '@tassat/forms';
import { AddressBookRecord } from '@tassat/fred-definitions';
import { Export as ExportIcon } from '@tassat/icon';
import { FormModal } from '@tassat/modal';
import React from 'react';
import ReactGA from 'react-ga4';

import { submitAddCounterpartyForm, validateWalletExists } from 'Containers/client/forms/helpers/addCounterpartyFormFn';
import Header from 'Containers/header';
import { exportClientFirmAddressBook } from 'Lib/exports/addressBookExports';
import { ApplicationMuiTheme } from 'Lib/themes/muiTheme';
import Worker from 'Lib/worker/PrimusWorker';
import { useAppSelector } from 'Reducers/store/hooks';
import { getAddressBookContactsArray } from 'Selectors/addressBook';
import { getAddressBook, getUser } from 'Selectors/common';
import { getClientFirm } from 'Selectors/firms';
import { PageHeaderTitle } from 'SharedComponents/pageHeader';

import AddressBookAgGrid from './bookAgGrid';
import ClientSearchContainer from './searchPanel/clientSearchContainer';
import type { SearchParameters } from './searchPanel/types';

export const PAGE_TITLE = 'Address Book';

const useAddressBookStyles = makeStyles((theme: ApplicationMuiTheme) => {
    return {
        container: {
            height: '100%',
            background: theme?.app?.applicationColors.backgroundPrimary ||
                '#FFFFFF'
        },
        header: {
            flexBasis: 'auto',
            flexShrink: 1
        },
        exportIcon: {
            height: theme.spacing(2.375),
            width: theme.spacing(4.75)
        },
        alignTable: { marginLeft: '1.9rem' },
        actionMenuItem: { marginRight: theme.spacing(2.5) },
        actionMenu: {
            paddingLeft: theme.spacing(4),
            paddingRight: theme.spacing(4.25)
        },
        footer: {
            position: 'absolute',
            bottom: '0px',
            height: '60px',
            left: '0px',
            right: '0px',
            overflow: 'hidden'
        }
    };
});

export function AddressBook() {
    const classes = useAddressBookStyles();
    const user = useAppSelector(getUser);

    const firm = useAppSelector(getClientFirm);
    const addressBookContacts = useAppSelector(getAddressBookContactsArray);
    const indexedContacts = useAppSelector(getAddressBook);

    React.useEffect(() => {
        Worker.getInstance()
            .getAddressBookContacts({ FirmId: firm.FirmId });
    }, [firm.FirmId]);

    const [ showPairingModal, setShowPairingModal ] = React.useState<boolean>(false);
    const [ areRecordsExpanded, setAreRecordsExpanded ] = React.useState<boolean>(false);
    const [ formType, setFormType ] = React.useState<ActionTypes>(ActionTypes.add);
    const [ searchParams, setSearchParams ] = React.useState<SearchParameters>({
        client: {
            value: '',
            groupedBy: ''
        }
    });

    const togglePairingModal = () => {
        setShowPairingModal(!showPairingModal);
    };

    const [ editContact, setEditContact ] = React.useState<AddressBookRecord | null>(null);
    const editContactForm = (contact) => {
        setEditContact(contact);
        setFormType(ActionTypes.edit);
        setShowPairingModal(true);
    };

    const editAddressBookContact = (contact): void => {
        editContactForm(contact);
    };

    const handleRecordsMinimizeOrExpansion = (): void => {
        ReactGA.event('click', {
            event_category: 'engagement',
            event_label: !areRecordsExpanded ? 'expand_records' : 'minimize_records'
        });
        setAreRecordsExpanded(!areRecordsExpanded);
    };

    const handleClientSearch = (searchParams: SearchParameters): void => {
        setSearchParams(searchParams);
    };

    return (
        <Grid
            className={classes.container}
            container
            direction='column'
            spacing={0}
            wrap='nowrap'
        >
            <Grid item>
                <Header />
            </Grid>
            <Grid
                className={classes.header}
                container
                direction='row'
                item
                wrap='nowrap'
                xs={12}
            >
                <PageHeaderTitle
                    form
                    onClick={() => {
                        ReactGA.event('click', {
                            event_category: 'engagement',
                            event_label: 'address_book_add_counterparty'
                        });
                        setShowPairingModal(!showPairingModal);
                    }}
                    title={PAGE_TITLE}
                />
            </Grid>
            {
                !!addressBookContacts.length &&
                <Grid
                    className={classes.actionMenu}
                    container
                    justifyContent='space-between'
                >
                    <Grid
                        item
                        xs={4}
                    >
                        <ClientSearchContainer
                            data={addressBookContacts}
                            onSearch={handleClientSearch}
                        />
                    </Grid>
                    <Grid
                        container
                        item
                        justifyContent='flex-end'
                        xs={6}
                    >
                        <Grid
                            className={classes.actionMenuItem}
                            item
                        >
                            <ExpandMinimizeButton
                                isExpanded={areRecordsExpanded}
                                onClick={handleRecordsMinimizeOrExpansion}
                            />
                        </Grid>
                        <Grid item>
                            <ToolbarButton
                                Icon={ExportIcon}
                                iconClassName={classes.exportIcon}
                                onClick={() => {
                                    ReactGA.event('click', {
                                        event_category: 'engagement',
                                        event_label: 'address_book_export'
                                    });
                                    exportClientFirmAddressBook();
                                }}
                                text='Export Address Book'
                            />
                        </Grid>
                    </Grid>
                </Grid>
            }
            <Box mt={4} />
            <AddressBookAgGrid
                areRecordsExpanded={areRecordsExpanded}
                contacts={addressBookContacts}
                editContact={editAddressBookContact}
                searchParams={searchParams}
                user={user}
            />
            <FormModal
                Form={AddCounterpartyForm}
                formProps={{
                    actionType: formType,
                    contactForEdit: editContact,
                    firm,
                    user,
                    addressBookContacts,
                    selectedContact: editContact && editContact.AddressBookId && indexedContacts.contacts[editContact.AddressBookId],
                    validateWalletExists: validateWalletExists,
                    submitAddCounterpartyForm: submitAddCounterpartyForm
                }}
                onClose={() => {
                    togglePairingModal();
                    setFormType(ActionTypes.add);
                    setEditContact(null);
                }}
                open={showPairingModal}
                title={`${formType === ActionTypes.add ? 'Add New' : 'Edit'} Counterparty`}
            />
        </Grid>
    );
}